// ── Shared helper functions ───────────────────────────────────────────────────

// ── Photo utilities ───────────────────────────────────────────────────────────

/** Max dimension (px) for stored profile photos. Keeps base64 small enough to
 *  fit in a Supabase JWT without triggering HTTP 494 header-too-large errors. */
export const PHOTO_MAX_PX = 200;

/**
 * Resize a data-URL image to at most PHOTO_MAX_PX × PHOTO_MAX_PX as JPEG @ 60%.
 * Returns a Promise that resolves to the resized data URL, or null if decoding fails.
 */
export function shrinkDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, PHOTO_MAX_PX / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.6));
      } catch (_) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Read a File, resize it via shrinkDataUrl, and return the resulting data URL.
 * Returns a Promise resolving to the data URL string, or null on any failure.
 */
export function resizeFileToDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => shrinkDataUrl(ev.target.result).then(resolve);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/** Derive 1-2 char initials from a display name */
export function deriveIni(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length-1][0]).toUpperCase();
}

/** Human-readable relative time for a notification timestamp */
export function notifRelTime(isoStr) {
  if (!isoStr) return "Recently";
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h/24)}d ago`;
}

/** Format an anniversary date and compute days until next occurrence */
export function fmtAnniversary(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date();
  const thisYear = new Date(now.getFullYear(), d.getMonth(), d.getDate());
  const nextAnniv = thisYear < now
    ? new Date(now.getFullYear()+1, d.getMonth(), d.getDate())
    : thisYear;
  const diffDays = Math.round((nextAnniv - now) / (1000*60*60*24));
  const years = nextAnniv.getFullYear() - d.getFullYear();
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const label = `${monthNames[d.getMonth()]} ${d.getDate()}`;
  const upcoming = diffDays <= 30;
  return { label, years, diffDays, upcoming, yearStr: `${years} yr${years!==1?"s":""}` };
}

/** Format a birthday and compute days until next */
export function fmtBirthday(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date();
  const thisYear = new Date(now.getFullYear(), d.getMonth(), d.getDate());
  const next = thisYear < now
    ? new Date(now.getFullYear()+1, d.getMonth(), d.getDate())
    : thisYear;
  const diffDays = Math.round((next - now) / (1000*60*60*24));
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const label = `${monthNames[d.getMonth()]} ${d.getDate()}`;
  const upcoming = diffDays <= 30;
  const age = next.getFullYear() - d.getFullYear() - (diffDays > 0 ? 1 : 0);
  return { label, diffDays, upcoming, age, ageStr: `${age} yr${age!==1?"s":""}` };
}
